import { rootMargin } from '@helpers/animate';
import { InterfaceIcon, getSvgIcon } from '@helpers/icons';
import { highlightTitleWords } from '@helpers/utils';
import { withMotion } from '@hoc/withMotion';
import { useInView } from '@hooks/useInView';
import SvgBRC from '@img/logos/brc-white.svg';
import SvgNIRC from '@img/logos/nirc-white.svg';
import SvgSRC from '@img/logos/src-white.svg';
import SvgWRC from '@img/logos/wrc-white.svg';
import { LinkButton } from '@stories/Components/Buttons/Button/Button';
import RegionSelector from '@stories/Components/Misc/RegionSelector/RegionSelector';
import { BackgroundVariant } from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SocialLinks from '@stories/Components/Misc/SocialLinks/SocialLinks';
import * as React from 'react';
import { Site } from '../Header/Header';
import S from './Footer.styles';
const Footer = (props) => {
    const [open, setOpen] = React.useState(-1);
    const [containerRef, inView] = useInView({ rootMargin }, false, props.pageContext?.isEditMode);
    const averageLinks = React.useMemo(() => props.linkSections.flatMap((s) => s.links).length / props.linkSections.length, [props.linkSections]);
    return (React.createElement(S.Container, { ref: containerRef, "data-animate": inView },
        React.createElement(S.Inner, null,
            React.createElement(S.Logo, { href: props.site.url, "aria-label": props.site.label }, renderLogo(props.site.type)),
            props.strapline1 && React.createElement(S.Strapline, null, props.strapline1),
            props.strapline2 && (React.createElement(S.StraplineLarge, { dangerouslySetInnerHTML: { __html: highlightTitleWords(props.strapline2) } })),
            props.buttons && props.buttons.length > 0 && (React.createElement(S.ButtonWrapper, { "data-variant": BackgroundVariant.Purple }, props.buttons.map((button, i) => (React.createElement(LinkButton, { key: `footer-button-${i}`, buttonType: button.buttonType ? button.buttonType : i === 0 ? 'secondary' : 'primary', href: button.url, target: button.target, icon: button.icon }, button.text))))),
            props.linkSections && props.linkSections.length > 0 ? (React.createElement(S.LinkListContainer, null, props.linkSections.map((group, i) => (React.createElement("div", { key: `footer-links-${group.id}`, "data-long": group.links.length > averageLinks },
                React.createElement(S.LinkListHeader, { "data-open": open === i, onClick: () => {
                        if (open === i) {
                            setOpen(-1);
                        }
                        else {
                            setOpen(i);
                        }
                    }, "aria-controls": `footer-links-${group.id}` },
                    React.createElement("h2", null, group.label.text),
                    getSvgIcon(InterfaceIcon.Chevron)),
                React.createElement(S.LinkListTitle, null,
                    React.createElement(S.LinkListHeader, { ...(!group.label.url
                            ? { as: 'span' }
                            : { as: 'a', href: group.label.url, target: group.label.url }) }, group.label.text)),
                React.createElement(S.LinkList, { id: `footer-links-${group.id}`, variants: {
                        initial: {
                            height: 0,
                            marginBottom: 0,
                            transitionEnd: { visibility: 'hidden' },
                        },
                        open: { height: 'auto', marginBottom: 24, visibility: 'visible' },
                    }, animate: open === i ? 'open' : 'initial' },
                    group.label.url && (React.createElement("li", { "data-mobile-only": true },
                        React.createElement("a", { href: group.label.url, target: group.label.target }, group.label.text))),
                    group.links.map((link, j) => (React.createElement("li", { key: `footer-links-${group.id}-${j}` },
                        React.createElement("a", { href: link.url, target: link.target }, link.text)))))))))) : null,
            React.createElement(RegionSelector, { ...props.regionSelector }),
            props.socialLinks && props.socialLinks.length > 0 && (React.createElement(S.SocialLinks, null,
                React.createElement(S.GroupTitle, null, props.translations?.['global.footer.socialLinks'] ?? ''),
                React.createElement(SocialLinks, { items: props.socialLinks }))),
            props.enquiries && props.enquiries.length > 0 && (React.createElement(React.Fragment, null, props.enquiries.map((enquiry, i) => (React.createElement(S.Enquiry, { key: `footer-enquiries-${i}` },
                React.createElement(S.GroupTitle, null, enquiry.heading),
                React.createElement(S.LinkList, null, enquiry.links.map((link, j) => (React.createElement("li", { key: `footer-enquiries-${i}-${j}` },
                    React.createElement("a", { href: `${link.icon === InterfaceIcon.Mail
                            ? 'mailto:'
                            : link.icon === InterfaceIcon.Phone
                                ? 'tel:'
                                : ''}${link.url}`, target: link.target },
                        getSvgIcon(link.icon),
                        link.text)))))))))),
            (props.copyrightText || props.legalLinks) && (React.createElement(S.Copyright, null,
                props.copyrightText && props.copyrightText.length > 0 && (React.createElement("p", null, props.copyrightText.map((cr, i) => (React.createElement("span", { key: `footer-copyright-${i}` }, cr))))),
                props.legalLinks && props.legalLinks.length > 0 && (React.createElement("ul", null, props.legalLinks.map((link, i) => (React.createElement("li", { key: `footer-legal-links-${i}` },
                    React.createElement("a", { href: link.url, target: link.target }, link.text)))))))),
            React.createElement(S.Divider, { role: "separator", "data-first": true }),
            React.createElement(S.Divider, { role: "separator", "data-second": true }),
            React.createElement(S.Divider, { role: "separator", "data-third": true }))));
    function renderLogo(site) {
        switch (site) {
            case Site.BRC:
                return React.createElement(SvgBRC, null);
            case Site.NIRC:
                return React.createElement(SvgNIRC, null);
            case Site.SRC:
                return React.createElement(SvgSRC, null);
            case Site.WRC:
                return React.createElement(SvgWRC, null);
        }
    }
};
export default withMotion(Footer);
